import type { ChangesSinceLastVisit, PaginationMeta } from '../types'
import type { Audit } from '@app/shared/types/sf-data'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getAudits, getChangesSinceLastVisit } from '../api'
import { SfLog } from '@app/adapters/logger'
import type { UIStateType } from '@app/shared/constants'
import { UIState } from '@app/shared/constants'
import type { SilverfinContext } from '@app/shared/types/sf-data'

export const useAuditsStore = defineStore('audits', () => {
  const audits = ref<Audit[]>([])
  const changesSinceLastVisit = ref<ChangesSinceLastVisit>({
    count: 0,
    since: 0,
  })
  const uiStatus = ref<UIStateType>(UIState.DEFAULT)
  const pagination = ref<PaginationMeta | null>(null)
  const isFetchingMore = ref(false)
  const contextGeneration = ref(0)

  const isFetchingAudits = computed(() => uiStatus.value === UIState.LOADING)

  const hasMorePages = computed(() => {
    if (!pagination.value) return false
    return pagination.value.currentPage < pagination.value.totalPages
  })

  async function fetchAudits({
    contextId,
    contextType,
  }: {
    contextId: number
    contextType: SilverfinContext.Account | SilverfinContext.ReconciliationText
  }): Promise<void> {
    contextGeneration.value += 1
    isFetchingMore.value = false
    uiStatus.value = UIState.LOADING
    pagination.value = null

    try {
      const data = await getAudits({ contextId, contextType, page: 1 })

      if (!data) {
        throw new Error('Failed to load audits')
      }

      audits.value = data.audits
      pagination.value = data.pagination ?? null

      if (data?.audits?.length === 0) {
        uiStatus.value = UIState.EMPTY
      } else {
        uiStatus.value = UIState.DEFAULT
      }
    } catch (error) {
      uiStatus.value = UIState.ERROR
      SfLog.info('Failed to load audits', {
        extra: {
          contextId,
          contextType,
          error,
        },
      })
    }
  }

  async function fetchNextPage({
    contextId,
    contextType,
  }: {
    contextId: number
    contextType: SilverfinContext.Account | SilverfinContext.ReconciliationText
  }): Promise<void> {
    if (!hasMorePages.value || isFetchingMore.value) return

    const nextPage = (pagination.value?.currentPage ?? 0) + 1
    isFetchingMore.value = true
    const generation = contextGeneration.value

    try {
      const data = await getAudits({ contextId, contextType, page: nextPage })

      if (!data) {
        throw new Error('Failed to load more audits')
      }

      if (generation !== contextGeneration.value) return

      audits.value = [...audits.value, ...data.audits]
      pagination.value = data.pagination ?? null
    } catch (error) {
      if (generation !== contextGeneration.value) return

      SfLog.info('Failed to load more audits', {
        extra: {
          contextId,
          contextType,
          page: nextPage,
          error,
        },
      })
    } finally {
      if (generation === contextGeneration.value) {
        isFetchingMore.value = false
      }
    }
  }

  async function fetchChangesSinceLastVisit({
    contextId,
    contextType,
  }: {
    contextId: number
    contextType: SilverfinContext.Account | SilverfinContext.ReconciliationText
  }): Promise<void> {
    if (!contextId || !contextType) {
      changesSinceLastVisit.value = { count: 0, since: 0 }
      return
    }

    try {
      const data = await getChangesSinceLastVisit({ contextId, contextType })

      if (data?.changesSinceLastVisit) {
        changesSinceLastVisit.value = data.changesSinceLastVisit
      } else {
        changesSinceLastVisit.value = {
          count: 0,
          since: 0,
        }
      }
    } catch (error) {
      SfLog.info('Failed to load changes since last visit', {
        extra: {
          contextId,
          contextType,
          error,
        },
      })
    }
  }

  return {
    audits,
    changesSinceLastVisit,
    fetchAudits,
    fetchChangesSinceLastVisit,
    fetchNextPage,
    hasMorePages,
    isFetchingAudits,
    isFetchingMore,
    pagination,
    uiStatus,
  }
})
